import clsx from "clsx";
import Image from "next/image";
import React from "react";
import { useLocale } from "next-intl";
import { sendGTMEvent } from "@next/third-parties/google";
import { Button } from "@/components/ui/Button";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useRouter } from "@/i18n/navigation";
import { formatFerryPrice } from "@/utils/ferry/priceUtils";
import { generateFerrySearchUrl } from "@/utils/searchUrlGenerators";
import ferryServices from "@/services/ferryServices";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";

function RouteCard(props) {
  const { item, dataLayerPayload } = props;

  const {
    date,
    logo,
    fromCity,
    fromStation,
    fromTime,
    duration,
    toCity,
    toStation,
    toTime,
    price,
    searchData,
    isCouponSale,
    vehicle,
    redirectUrl,
  } = item;

  const t = useTranslationDetector("");
  const { currency } = useCurrency();
  const locale = useLocale();
  const router = useRouter();
  const href = searchData
    ? generateFerrySearchUrl(searchData)
    : redirectUrl || "#";
  const hasSentViewRef = React.useRef(false);
  const [isAllocatingCouponSale, setIsAllocatingCouponSale] =
    React.useState(false);

  const basePayload = React.useMemo(() => {
    if (!dataLayerPayload) return null;

    return {
      recommendation_id: dataLayerPayload.recommendation_id,
      recommendation_name: dataLayerPayload.recommendation_name || "ferry",
      recommendation_brand: dataLayerPayload.recommendation_brand || "",
      recommendation_category: dataLayerPayload.recommendation_category || "",
      recommendation_type: dataLayerPayload.recommendation_type || "domestic",
      start_date: dataLayerPayload.start_date || "",
      end_date: dataLayerPayload.end_date || "",
    };
  }, [dataLayerPayload]);

  const parseCampaignIdFromUrl = React.useCallback(() => {
    try {
      if (typeof window === "undefined") return [];
      const search = window.location.search || "";
      const params = new URLSearchParams(search);
      const raw = params.get("campaignId");
      if (!raw) return [];
      return String(raw)
        .split(",")
        .map((v) => v.trim())
        .filter((v) => v.length > 0);
    } catch {
      return [];
    }
  }, []);

  const handleSelectRecommendationEvent = React.useCallback(() => {
    if (!basePayload) return;
    sendGTMEvent({
      event: "custom_event",
      event_category: "recommendation",
      event_action: "select_recommendation",
      ...basePayload,
    });
  }, [basePayload]);

  const handleCouponSaleClick = React.useCallback(async () => {
    if (!searchData) {
      // Coupon sale akışında arama URL'i yerine allocateCouponSale kullanılır.
      // searchData yoksa güvenli şekilde çık.
      // eslint-disable-next-line no-console
      console.error("RouteCard coupon sale: missing searchData", item);
      return;
    }

    if (isAllocatingCouponSale) return;
    setIsAllocatingCouponSale(true);

    try {
      handleSelectRecommendationEvent();

      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);

      const allocatePayload = {
        searchForm: {
          SelectedCampaigns: parseCampaignIdFromUrl(),
          SelectedDepartureTripId: "",
          SelectedReturnTripId: "",
          paxes: [{ type: "ADT", age: 30 }],
          legs: [
            {
              origin: searchData?.origin?.id,
              destination: searchData?.destination?.id,
              departureDay: tomorrow,
            },
          ],
          vehicles: [{ Id: vehicle.Id, Count: 1 }],
          TicketCount: 1,
          SelectedSorting: {},
          SelectedFilters: {
            DepartureTimeRange: {},
            ArrivalTimeRange: {},
            ReturnDepartureTimeRange: {},
            ReturnArrivalTimeRange: {},
            Companies: [],
          },
        },
      };

      const allocateCouponSaleResponse =
        await ferryServices.search.allocateCouponSale(allocatePayload, {
          "Accept-Language": locale,
        });

      if (allocateCouponSaleResponse?.BasketId) {
        router.push(
          `/ferry/reservation/${allocateCouponSaleResponse.BasketId}`,
        );
      }
    } finally {
      setIsAllocatingCouponSale(false);
    }
  }, [
    handleSelectRecommendationEvent,
    isAllocatingCouponSale,
    item,
    locale,
    parseCampaignIdFromUrl,
    router,
    searchData,
  ]);

  React.useEffect(() => {
    if (!basePayload || hasSentViewRef.current) return;

    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry?.isIntersecting || hasSentViewRef.current) return;

        hasSentViewRef.current = true;
        sendGTMEvent({
          event: "custom_event",
          event_category: "recommendation",
          event_action: "view_recommendation",
          ...basePayload,
        });
      },
      { threshold: 0.2, rootMargin: "0px 0px -50px 0px" },
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, [basePayload]);

  const containerRef = React.useRef(null);

  const handleClick = React.useCallback(() => {
    handleSelectRecommendationEvent();
  }, [handleSelectRecommendationEvent]);

  const handleDefaultCardClick = React.useCallback(() => {
    handleClick();
    window.location.href = href;
  }, [handleClick, href]);

  const cardInner = (
    <>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <figure className="relative !h-12 !w-12 flex-shrink-0 overflow-hidden">
            <Image
              src={logo}
              width={48}
              height={48}
              alt="logo"
              className="h-full w-full object-contain"
            />
          </figure>
        </div>
        {!isCouponSale ? (
          <span className="text-sm leading-[19px]">{date}</span>
        ) : (
          <span className="text-sm leading-[19px]">{vehicle.Name}</span>
        )}
      </div>
      <div>
        <div className="flex w-full flex-col gap-1">
          <div className="relative grid w-full grid-cols-[1fr_auto_1fr] items-center gap-2 overflow-hidden">
            <div className="text-foreground-primary z-10 justify-self-start bg-white text-lg font-semibold">
              <p
                className={clsx(
                  "truncate text-lg leading-[26px] font-semibold",
                  "[@media(min-width:400px)]:w-[85px]",
                  "[@media(min-width:720px)]:w-[85px]",
                  "lg:w-[110px]!",
                  "text-left",
                )}
              >
                {fromCity}
              </p>
            </div>
            <span
              className={clsx(
                "bg-background-300 text-foreground-muted relative mx-2 flex h-[26px] items-center rounded-sm px-[8px] text-center text-xs leading-[26px] font-medium whitespace-nowrap",
                // before pseudo-element
                "before:border-border-card before:absolute before:top-1/2 before:right-full before:mr-2 before:w-[50px] before:-translate-y-1/2 before:border-t before:content-[''] md:before:w-[150px]",
                // after pseudo-element
                "after:border-border-card after:absolute after:top-1/2 after:left-full after:ml-2 after:w-[50px] after:-translate-y-1/2 after:border-t after:content-[''] md:after:w-[150px]",
              )}
            >
              {duration}
            </span>
            <div className="text-foreground-primary z-10 justify-self-end bg-white text-lg font-semibold">
              <p
                className={clsx(
                  "truncate text-right text-lg font-semibold",
                  "[@media(min-width:400px)]:w-[85px]",
                  "[@media(min-width:720px)]:w-[85px]",
                  "lg:w-[110px]!",
                )}
              >
                {toCity}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-foreground-muted">{fromStation}</p>
            {!isCouponSale && <p className="mt-1 font-medium">{fromTime}</p>}
          </div>

          <div className="text-right">
            <p className="text-foreground-muted">{toStation}</p>
            {!isCouponSale && <p className="mt-1 font-medium">{toTime}</p>}
          </div>
        </div>
      </div>
      <div className="border-primary-weak w-full border-t"></div>
      <div className="flex items-center justify-between">
        <p className="text-lg font-medium">
          {formatFerryPrice(price, currency, true)}
        </p>
        <Button className="flex h-[36px] flex-row items-center px-4 lg:px-4">
          <span className="text-sm">{t("common.buyNow")}</span>
        </Button>
      </div>
    </>
  );

  if (isCouponSale) {
    return (
      <div
        onClick={handleCouponSaleClick}
        disabled={isAllocatingCouponSale}
        className="border-primary-weak flex w-full cursor-pointer flex-col gap-[16px] rounded-xl border bg-white px-[24px] py-[20px] no-underline shadow-sm disabled:opacity-70"
        ref={containerRef}
      >
        {cardInner}
      </div>
    );
  }

  return (
    <div
      onClick={handleDefaultCardClick}
      className="border-primary-weak flex w-full cursor-pointer flex-col gap-[16px] rounded-xl border bg-white px-[24px] py-[20px] text-left no-underline shadow-sm"
      ref={containerRef}
    >
      {cardInner}
    </div>
  );
}

export default RouteCard;
